import React, { useMemo, useState, useEffect } from 'react';
import { X, Plus, Trash2, Calendar, Clock, Activity, Droplets, TrendingUp, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import {
  IrrigationProgram,
  ProgramType,
  ScheduleMode,
  TimeRange,
  DAYS_FR,
  rangesOverlap,
  timelineHours,
  simulateImpact,
  rangeDurationMinutes,
  useIrrigation,
} from '../contexts/IrrigationContext';

interface ProgramModalProps {
  editing: IrrigationProgram | null;
  onClose: () => void;
}

interface FormState {
  zone_id: string;
  name: string;
  description: string;
  type: ProgramType;
  mode: ScheduleMode;
  days: number[];
  cycle_days: number;
  time_ranges: TimeRange[];
  enabled: boolean;
}

const trId = () => `tr-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

const buildInitial = (editing: IrrigationProgram | null, defaultZoneId: string): FormState => {
  if (editing) {
    return {
      zone_id: editing.zone_id,
      name: editing.name,
      description: editing.description,
      type: editing.type,
      mode: editing.mode,
      days: [...editing.days],
      cycle_days: editing.cycle_days,
      time_ranges: editing.time_ranges.map((r) => ({ ...r })),
      enabled: editing.enabled,
    };
  }
  return {
    zone_id: defaultZoneId,
    name: '',
    description: '',
    type: 'irrigation',
    mode: 'weekly',
    days: [0, 1, 2, 3, 4],
    cycle_days: 2,
    time_ranges: [{ id: trId(), start: '06:00', end: '07:00' }],
    enabled: true,
  };
};

const ProgramModal: React.FC<ProgramModalProps> = ({ editing, onClose }) => {
  const { zones, addProgram, updateProgram, zoneById } = useIrrigation();
  const [form, setForm] = useState<FormState>(buildInitial(editing, zones[0]?.id ?? ''));

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const overlap = useMemo(() => rangesOverlap(form.time_ranges), [form.time_ranges]);
  const cells = useMemo(() => timelineHours(form.time_ranges), [form.time_ranges]);
  const flow = zoneById(form.zone_id)?.flow_rate_lpm ?? 20;
  const impact = useMemo(
    () => simulateImpact(form.time_ranges, { mode: form.mode, days: form.days, cycle_days: form.cycle_days }, flow),
    [form.time_ranges, form.mode, form.days, form.cycle_days, flow]
  );

  const toggleDay = (d: number) =>
    setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  const updateRange = (id: string, key: 'start' | 'end', value: string) =>
    setForm((f) => ({ ...f, time_ranges: f.time_ranges.map((r) => (r.id === id ? { ...r, [key]: value } : r)) }));

  const addRange = () =>
    setForm((f) => ({ ...f, time_ranges: [...f.time_ranges, { id: trId(), start: '08:00', end: '09:00' }] }));

  const removeRange = (id: string) =>
    setForm((f) => ({ ...f, time_ranges: f.time_ranges.filter((r) => r.id !== id) }));

  const handleSubmit = () => {
    if (!form.zone_id) return toast.error('Veuillez sélectionner une zone.');
    if (!form.name.trim()) return toast.error('Veuillez nommer le programme.');
    if (form.time_ranges.length === 0) return toast.error('Ajoutez au moins une plage horaire.');
    if (form.time_ranges.some((r) => rangeDurationMinutes(r) === 0))
      return toast.error("Une plage horaire a une durée nulle.");
    if (overlap) return toast.error('Les plages horaires ne doivent pas se chevaucher.');
    if (form.mode === 'weekly' && form.days.length === 0)
      return toast.error('Sélectionnez au moins un jour d\'activation.');
    if (form.mode === 'cyclic' && form.cycle_days < 1) return toast.error('Le cycle doit être d\'au moins 1 jour.');

    const payload = {
      zone_id: form.zone_id,
      name: form.name.trim(),
      description: form.description.trim(),
      type: form.type,
      mode: form.mode,
      days: form.days,
      cycle_days: form.cycle_days,
      time_ranges: form.time_ranges,
      enabled: form.enabled,
    };
    if (editing) {
      updateProgram(editing.id, payload);
      toast.success(`Programme « ${payload.name} » mis à jour.`);
    } else {
      addProgram(payload);
      toast.success(`Programme « ${payload.name} » créé.`);
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="program-modal-overlay"
    >
      <div
        className="bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-2xl my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="program-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-light)] sticky top-0 bg-[var(--surface)] rounded-t-2xl z-10">
          <h2 className="text-heading-4 text-[var(--text-primary)]">
            {editing ? 'Modifier le programme' : 'Nouveau programme'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--gray-100)]" data-testid="program-modal-close">
            <X className="w-5 h-5 text-[var(--text-secondary)]" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Zone */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Zone</label>
            <select
              value={form.zone_id}
              onChange={(e) => setForm({ ...form, zone_id: e.target.value })}
              className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2.5 bg-[var(--surface)] text-[var(--text-primary)]"
              data-testid="program-zone-select"
            >
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} — {z.field_name}
                </option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Nom du programme</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex : Irrigation matinale"
              className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2.5 bg-[var(--surface)] text-[var(--text-primary)]"
              data-testid="program-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description optionnelle"
              className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2.5 bg-[var(--surface)] text-[var(--text-primary)]"
              data-testid="program-description-input"
            />
          </div>

          {/* Watering type */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Type d'arrosage</label>
            <div className="grid grid-cols-2 gap-3">
              {(['irrigation', 'fertigation'] as ProgramType[]).map((tp) => (
                <button
                  key={tp}
                  type="button"
                  onClick={() => setForm({ ...form, type: tp })}
                  className={`px-4 py-2.5 rounded-lg border text-sm font-medium capitalize transition-colors ${
                    form.type === tp
                      ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                      : 'bg-[var(--surface)] text-[var(--text-secondary)] border-[var(--border-light)] hover:bg-[var(--gray-50)]'
                  }`}
                  data-testid={`program-type-${tp}`}
                >
                  {tp}
                </button>
              ))}
            </div>
          </div>

          {/* Program mode */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Type de programme</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, mode: 'weekly' })}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  form.mode === 'weekly'
                    ? 'bg-[var(--text-primary)] text-[var(--surface)]'
                    : 'bg-[var(--gray-50)] text-[var(--text-secondary)] hover:bg-[var(--gray-100)]'
                }`}
                data-testid="program-mode-weekly"
              >
                <Calendar className="w-4 h-4" /> Hebdomadaire
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, mode: 'cyclic' })}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  form.mode === 'cyclic'
                    ? 'bg-[var(--text-primary)] text-[var(--surface)]'
                    : 'bg-[var(--gray-50)] text-[var(--text-secondary)] hover:bg-[var(--gray-100)]'
                }`}
                data-testid="program-mode-cyclic"
              >
                <Clock className="w-4 h-4" /> Cyclique
              </button>
            </div>
          </div>

          {/* Weekly days OR cyclic interval */}
          {form.mode === 'weekly' ? (
            <div>
              <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Jours d'activation</label>
              <div className="grid grid-cols-7 gap-2">
                {DAYS_FR.map((d, idx) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`px-2 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      form.days.includes(idx)
                        ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                        : 'bg-[var(--surface)] text-[var(--text-secondary)] border-[var(--border-light)] hover:bg-[var(--gray-50)]'
                    }`}
                    data-testid={`program-day-${idx}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-[var(--text-primary)] mb-1.5">Répétition du cycle</label>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[var(--text-secondary)]">Tous les</span>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={form.cycle_days}
                  onChange={(e) => setForm({ ...form, cycle_days: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                  className="w-20 border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)] text-[var(--text-primary)] text-center"
                  data-testid="program-cycle-days"
                />
                <span className="text-sm text-[var(--text-secondary)]">jour(s)</span>
              </div>
            </div>
          )}

          {/* Time ranges */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-[var(--text-primary)]">Plages horaires</label>
              <button
                type="button"
                onClick={addRange}
                className="flex items-center gap-1 text-sm font-medium text-[var(--color-primary)] hover:opacity-80"
                data-testid="program-add-range"
              >
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>
            <div className="space-y-2 bg-[var(--gray-50)] rounded-lg p-3">
              {form.time_ranges.map((r) => (
                <div key={r.id} className="flex items-center gap-3" data-testid={`program-range-${r.id}`}>
                  <input
                    type="time"
                    value={r.start}
                    onChange={(e) => updateRange(r.id, 'start', e.target.value)}
                    className="flex-1 border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)] text-[var(--text-primary)]"
                    data-testid={`program-range-start-${r.id}`}
                  />
                  <span className="text-[var(--text-secondary)]">›</span>
                  <input
                    type="time"
                    value={r.end}
                    onChange={(e) => updateRange(r.id, 'end', e.target.value)}
                    className="flex-1 border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)] text-[var(--text-primary)]"
                    data-testid={`program-range-end-${r.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeRange(r.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-[var(--color-danger)]"
                    title="Supprimer la plage"
                    data-testid={`program-range-delete-${r.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {form.time_ranges.length === 0 && (
                <p className="text-sm text-[var(--text-tertiary)] text-center py-2">Aucune plage horaire</p>
              )}
            </div>
            {overlap && (
              <p className="text-sm text-[var(--color-danger)] mt-2" data-testid="program-overlap-error">
                Les plages horaires se chevauchent. Veuillez les corriger.
              </p>
            )}
          </div>

          {/* Timeline */}
          <div>
            <div className="flex gap-1">
              {cells.map((c, h) => (
                <div
                  key={h}
                  className={`flex-1 h-8 rounded ${
                    c === 'active'
                      ? 'bg-[var(--color-success)]'
                      : c === 'hot'
                      ? 'bg-amber-200'
                      : 'bg-[var(--gray-100)]'
                  }`}
                  title={`${h.toString().padStart(2, '0')}h`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-[var(--text-tertiary)] mt-1">
              <span>00h</span>
              <span>06h</span>
              <span>12h</span>
              <span>18h</span>
              <span>24h</span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-secondary)]">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-[var(--color-success)] inline-block" /> Actif
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-amber-200 inline-block" /> Heures chaudes (11h-15h)
              </span>
            </div>
          </div>

          {/* Impact simulé */}
          <div className="rounded-xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-[var(--color-success)]" />
              <span className="text-sm font-semibold text-[var(--text-primary)]">Impact simulé</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[var(--surface)] rounded-lg p-3 text-center" data-testid="impact-water">
                <Droplets className="w-5 h-5 mx-auto mb-1 text-[var(--color-info)]" />
                <div className="text-lg font-bold text-[var(--color-info)]">{impact.waterPerWeek} L</div>
                <div className="text-xs text-[var(--text-secondary)]">Eau/semaine</div>
              </div>
              <div className="bg-[var(--surface)] rounded-lg p-3 text-center" data-testid="impact-moisture">
                <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[var(--color-success)]" />
                <div className="text-lg font-bold text-[var(--color-success)]">+{impact.moisture}%</div>
                <div className="text-xs text-[var(--text-secondary)]">Humidité sol</div>
              </div>
              <div className="bg-[var(--surface)] rounded-lg p-3 text-center" data-testid="impact-plant">
                <Leaf className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                <div className="text-lg font-bold text-amber-500">{impact.plantImpact}</div>
                <div className="text-xs text-[var(--text-secondary)]">Impact plante</div>
              </div>
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center gap-2">
            <input
              id="program-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="w-4 h-4"
              data-testid="program-enabled"
            />
            <label htmlFor="program-enabled" className="text-sm text-[var(--text-primary)]">
              Activer ce programme immédiatement
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-[var(--border-light)] sticky bottom-0 bg-[var(--surface)] rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[var(--border-medium)] rounded-lg text-[var(--text-secondary)] hover:bg-[var(--gray-50)]"
            data-testid="program-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-5 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90"
            data-testid="program-submit"
          >
            {editing ? 'Enregistrer' : 'Créer le programme'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramModal;
